import React, { useState, useEffect, useRef, useContext } from 'react';
import { useFormContext, Controller, get, useWatch, Path } from 'react-hook-form';
import { Search, Paperclip, X, Trash2 } from 'lucide-react';

import { OrderSchemaType, ItemPath } from '../../../../order.schema';
import { useApiData } from '../../../../contexts/ApiDataContext';
import { useAttachmentHandler } from '../../../../Hooks/useAttachmentHandler';
import SteelOrdersInfiniteSelect from '../../../../components/SteelOrdersInfiniteSelect';
import ApiService from '../../../../../../../../services/ApiService';
import { NumericInput } from '../../../../../../OrderForm/components/NumericInput';
import QuickStageCostModal from '../../../../components/QuickStageCostModal';
import PendencyCallout from '../../../../components/PendencyCallout';
import CurrencyInput from '../../../../../../../../components/CurrencyInput';
import axios from '../../../../../../../../config/axios';
import AttachmentSourceModal from '../../../../../../../../components/AttachmentSourceModal';
import AttachmentGalleryModal, { GalleryFile } from '../../../../../../../../components/AttachmentGalleryModal';

import {
  FormGrid,
  FormGroup,
  HintText,
  Input,
  Label,
  Select,
  AttachmentList,
  AttachmentListItem,
  RemoveAttachmentButton,
  FormErrorMessage,
  AttachmentInputContainer,
  AttachmentTrigger,
  AttachmentPlaceholder,
  TrashIconContainer,
  Textarea,
} from '../../../../style';
import {
  ZeroCostRow,
  ZeroCostSwitch,
  ZeroCostInput,
  ZeroCostSlider,
  ZeroCostLabel,
  ZeroCostNotice,
} from './ServicesSection.style';
import { FeedbackContext } from '../../../../../../../../contexts/FeedbackContext';

interface ServicesSectionProps {
  itemPath: ItemPath;
}

const ServicesSection: React.FC<ServicesSectionProps> = ({ itemPath }) => {
  const { register, control, formState: { errors }, setValue, watch } = useFormContext<OrderSchemaType>();
  const { setFeedback } = useContext(FeedbackContext);

  const isSubItem = itemPath.includes('.subItems.');
  const parentItemPath = isSubItem ? itemPath.split('.subItems.')[0] : null;
  const parentItemType = useWatch({ control, name: `${parentItemPath}.itemType` as any });
  const isSimpleService = parentItemType === 'MATERIAL';

  const [selectedServiceCategory, setSelectedServiceCategory] = useState<{ id: number; name: string }[]>([]);
  const [selectedServiceType, setSelectedServiceType] = useState<{ id: number; name: string }[]>([]);
  const [selectedService, setSelectedService] = useState<{ id: number; name: string }[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<{ id: number; name: string }[]>([]);

  const parentQty = useWatch({ control, name: `${parentItemPath}.quantity` as any });
  const parentWeight = useWatch({ control, name: `${parentItemPath}.offeredProductMaterial.weigth` as any });
  const unitPrice = useWatch({ control, name: `${itemPath}.priceDetails.unitPrice` as any });
  const serviceUnit = useWatch({ control, name: `${itemPath}.priceDetails.serviceUnit` as any });

  const serviceCost = useWatch({ control, name: `${itemPath}.cost` as any });
  const serviceMarkup = useWatch({ control, name: `${itemPath}.markup` as any });
  const stageQty = useWatch({ control, name: `${itemPath}.stage.quantidade` as any });
  const watchedCostUnitName = useWatch({ control, name: `${itemPath}.costUnitName` as any });
  const hasCostSnapshot = (Number(serviceCost) || 0) > 0;

  const unitSourcePath = isSubItem ? parentItemPath : itemPath;
  const parentUnitId = useWatch({ control, name: `${unitSourcePath}.unitOfMeasurementId` as any });
  const parentUnitName = useWatch({ control, name: `${unitSourcePath}.unitOfMeasurementName` as any });

  const prevUnitRef = useRef<number | 'uninit'>('uninit');
  useEffect(() => {
    const unit = Number(parentUnitId) || 0;
    if (prevUnitRef.current === 'uninit') {
      prevUnitRef.current = unit;
      return;
    }
    if (prevUnitRef.current === unit) return;
    prevUnitRef.current = unit;

    const stageId = Number(watch(`${itemPath}.stageId` as any)) || Number(selectedService[0]?.id) || 0;
    if (!stageId) return;

    if (!unit) {
      // Unidade removida: limpa o snapshot — a pendência (SEM_UNIDADE) assume.
      setValue(`${itemPath}.cost` as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue(`${itemPath}.costUnitId` as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue(`${itemPath}.costUnitName` as any, '', { shouldDirty: true, shouldValidate: false });
      return;
    }

    let active = true;
    ApiService.steelOrders.getServices({ id: stageId, unitOfMeasurementId: unit })
      .then((res: any) => {
        if (!active) return;
        const svc = res.data?.services?.[0];
        if (!svc) return;
        const hasCost = svc.cost !== null && svc.cost !== undefined;
        setValue(`${itemPath}.allowZeroCost` as any, svc.allowZeroCost === true, { shouldDirty: true, shouldValidate: false });
        setValue(`${itemPath}.cost` as any, hasCost ? Number(svc.cost) : undefined, { shouldDirty: true, shouldValidate: false });
        setValue(`${itemPath}.markup` as any, svc.markup != null ? Number(svc.markup) : undefined, { shouldDirty: true, shouldValidate: false });
        setValue(`${itemPath}.costUnitId` as any, hasCost ? unit : undefined, { shouldDirty: true, shouldValidate: false });
        setValue(`${itemPath}.costUnitName` as any, hasCost ? (svc.costUnitName || parentUnitName || '') : '', { shouldDirty: true, shouldValidate: false });
      })
      .catch(() => { /* mantém o snapshot atual; a pendência sinaliza se faltar custo */ });
    return () => { active = false; };
  }, [parentUnitId]);

  const allowsZeroCost = watch(`${itemPath}.allowZeroCost` as any) === true;
  const missingCostInUnit = !!selectedService[0]?.id && !!parentUnitId && !hasCostSnapshot;
  const selectedWithoutCostInUnit = missingCostInUnit && !allowsZeroCost;
  const [zeroCostSaving, setZeroCostSaving] = useState(false);

  // A flag mora em Stage, não em OrderItem: marcar aqui altera o cadastro do serviço.
  const handleAllowZeroCostToggle = async (checked: boolean) => {
    const targetStageId = Number(watch(`${itemPath}.stageId` as any)) || Number(selectedService[0]?.id) || 0;
    if (!targetStageId || zeroCostSaving) return;
    setZeroCostSaving(true);
    setValue(`${itemPath}.allowZeroCost` as any, checked, { shouldDirty: true, shouldValidate: false });
    try {
      await ApiService.services.update(targetStageId, { allowZeroCost: checked });
    } catch (e: unknown) {
      setValue(`${itemPath}.allowZeroCost` as any, !checked, { shouldDirty: true, shouldValidate: false });
      setFeedback({ message: 'Não foi possível salvar a permissão de custo zero no cadastro do serviço.', isOpen: true, statusOk: false });
    } finally {
      setZeroCostSaving(false);
    }
  };

  const hasMarkupSnapshot = serviceMarkup !== undefined && serviceMarkup !== null && serviceMarkup !== '' && !isNaN(Number(serviceMarkup));
  const selectedWithoutMarkup = !!selectedService[0]?.id && !!parentUnitId && hasCostSnapshot && !hasMarkupSnapshot && !allowsZeroCost;
  const [costModalMode, setCostModalMode] = useState<null | 'cost' | 'markup'>(null);

  const handleQuickCostSaved = ({ cost, markup }: { cost?: number; markup?: number }) => {
    if (cost !== undefined) {
      setValue(`${itemPath}.cost` as any, cost, { shouldDirty: true, shouldValidate: false });
      setValue(`${itemPath}.costUnitId` as any, Number(parentUnitId), { shouldDirty: true, shouldValidate: false });
      setValue(`${itemPath}.costUnitName` as any, parentUnitName || '', { shouldDirty: true, shouldValidate: false });
    }
    if (markup !== undefined) {
      setValue(`${itemPath}.markup` as any, markup, { shouldDirty: true, shouldValidate: false });
    }
  };

  // Fórmula do serviço: (custo na unidade do item × quantidade) × markup.
  // Subitem herda a quantidade do ITEM pai (acoplamento da unidade soberana — vale para
  // MP e PE); serviço como item avulso usa a própria quantidade.
  // Preço final é editável (snapshot); na hidratação de pedido salvo o valor persistido prevalece.
  const priceInitSyncRef = useRef(false);
  useEffect(() => {
    const cost = Number(serviceCost) || 0;
    const qty = isSubItem ? (Number(parentQty) || 0) : (Number(stageQty) || 0);
    if (!(cost > 0 && qty > 0)) return;
    const markup = (Number(serviceMarkup) || 0) > 0 ? Number(serviceMarkup) : 1;
    const price = parseFloat((cost * qty * markup).toFixed(2));

    if (!priceInitSyncRef.current) {
      priceInitSyncRef.current = true;
      const existing = watch(`${itemPath}.itemPrice` as any) as any;
      if (existing !== undefined && existing !== null && Number(existing) > 0) {
        // Pedido salvo com preço ajustado manualmente — não recalcular na hidratação.
        return;
      }
    }

    const current = watch(`${itemPath}.itemPrice` as any) as any;
    if (Number(current) !== price) {
      setValue(`${itemPath}.itemPrice` as any, price, { shouldDirty: true, shouldValidate: false });
      setValue(`${itemPath}.priceDetails.itemSubtotal` as any, price, { shouldDirty: true, shouldValidate: false });
    }
  }, [serviceCost, serviceMarkup, stageQty, parentQty, isSimpleService, itemPath, setValue, watch]);

  // Fallback legado (serviço sem custo cadastrado): cálculo antigo baseado em unitPrice/unidade.
  useEffect(() => {
    if (isSimpleService && unitPrice !== undefined && !hasCostSnapshot) {
      const numQty = Number(parentQty) || 0;
      const numWeight = parseFloat(String(parentWeight) || '0');
      const sPrice = Number(unitPrice) || 0;
      const sUnit = String(serviceUnit || '').toUpperCase();

      let total = 0;
      if (sPrice > 0 && numQty > 0) {
        if (sUnit === 'KG' && numWeight > 0) {
          total = sPrice * numWeight * numQty;
        } else {
          total = sPrice * numQty;
        }
      }
      setValue(`${itemPath}.priceDetails.itemSubtotal` as any, parseFloat(total.toFixed(2)), { shouldDirty: true, shouldValidate: false });
    }
  }, [parentQty, parentWeight, unitPrice, serviceUnit, isSimpleService, hasCostSnapshot, itemPath, setValue]);

  const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`;

  const [isSourceChoiceOpen, setIsSourceChoiceOpen] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  const {
    attachments,
    attachmentError,
    fileInputRef,
    handleFileSelectClick,
    handleFileChange,
    handleSelectFromGallery,
    handleRemoveAttachment,
    handleRemoveAllAttachments,
  } = useAttachmentHandler({ fieldPath: `${itemPath}.stage.anexos` });

  const stagePath: `${ServicesSectionProps['itemPath']}.stage` = `${itemPath}.stage`;

  useEffect(() => {
    const existingStageId = watch((`${itemPath}.stageId`) as any) as number | undefined;
    if (selectedService.length > 0) {
      setValue(`${stagePath}.servico`, selectedService[0].name);
      setValue((`${itemPath}.stageId`) as any, selectedService[0].id, { shouldDirty: true, shouldValidate: false });

      const observation = (selectedService[0] as any).observation;
      const prices = (selectedService[0] as any).prices || [];

      let price = (selectedService[0] as any).price;
      let unit = (selectedService[0] as any).unit;

      const parentUnit = String(watch(`${parentItemPath}.feedstock.unit` as any) || '').toUpperCase();

      if (parentUnit && prices.length > 0) {
        const matched = prices.find((p: any) => p.unit === parentUnit);
        if (matched) {
          price = matched.price;
          unit = matched.unit;
        }
      }

      if (existingStageId !== selectedService[0].id) {
        if (observation) {
          setValue(`${stagePath}.observacoes` as any, observation, { shouldDirty: true, shouldValidate: false });
        } else {
          setValue(`${stagePath}.observacoes` as any, '', { shouldDirty: true, shouldValidate: false });
        }

        const svc: any = selectedService[0];
        const svcHasCost = svc.cost !== undefined && svc.cost !== null && Number(svc.cost) > 0;
        setValue((`${itemPath}.allowZeroCost`) as any, svc.allowZeroCost === true, { shouldDirty: true, shouldValidate: false });
        if (svcHasCost) {
          setValue((`${itemPath}.cost`) as any, Number(svc.cost) || 0, { shouldDirty: true, shouldValidate: false });
          setValue((`${itemPath}.markup`) as any, (svc.markup !== undefined && svc.markup !== null) ? Number(svc.markup) : undefined, { shouldDirty: true, shouldValidate: false });
          setValue((`${itemPath}.costUnitId`) as any, svc.costUnitId ?? undefined, { shouldDirty: true, shouldValidate: false });
          setValue((`${itemPath}.costUnitName`) as any, svc.costUnitName ?? '', { shouldDirty: true, shouldValidate: false });
          setValue(`${itemPath}.priceDetails.unitPrice` as any, 0, { shouldDirty: true, shouldValidate: false });
          setValue(`${itemPath}.priceDetails.serviceUnit` as any, svc.costUnitName || '', { shouldDirty: true, shouldValidate: false });
        } else {
          // Fallback legado — serviço sem custo cadastrado: comportamento antigo via StagePrice.
          setValue((`${itemPath}.cost`) as any, undefined, { shouldDirty: true, shouldValidate: false });
          setValue((`${itemPath}.markup`) as any, undefined, { shouldDirty: true, shouldValidate: false });
          setValue((`${itemPath}.costUnitId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
          setValue((`${itemPath}.costUnitName`) as any, '', { shouldDirty: true, shouldValidate: false });
          setValue(`${itemPath}.priceDetails.unitPrice` as any, Number(price) || 0, { shouldDirty: true, shouldValidate: false });
          setValue(`${itemPath}.priceDetails.serviceUnit` as any, unit || '', { shouldDirty: true, shouldValidate: false });
        }

        setSelectedServiceCategory([]);
        setSelectedServiceType([]);
        setSelectedSupplier([]);
        setValue((`${itemPath}.serviceCategoryId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
        setValue((`${itemPath}.serviceTypeId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
        setValue((`${stagePath}.categoria`) as any, '', { shouldDirty: true, shouldValidate: false });
        setValue((`${stagePath}.tipo`) as any, '', { shouldDirty: true, shouldValidate: false });
        setValue((`${itemPath}.serviceSupplierCompanyId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
        setValue((`${itemPath}.stage.fornecedor`) as any, '', { shouldDirty: true, shouldValidate: false });
      }
    } else {
      setValue(`${stagePath}.servico`, '');
      if (existingStageId === undefined || existingStageId === null) {
        setValue((`${itemPath}.stageId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      }
      setSelectedServiceCategory([]);
      setSelectedServiceType([]);
      setSelectedSupplier([]);
      setValue((`${itemPath}.serviceCategoryId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${itemPath}.serviceTypeId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${stagePath}.categoria`) as any, '', { shouldDirty: true, shouldValidate: false });
      setValue((`${stagePath}.tipo`) as any, '', { shouldDirty: true, shouldValidate: false });
      setValue((`${itemPath}.serviceSupplierCompanyId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${itemPath}.stage.fornecedor`) as any, '', { shouldDirty: true, shouldValidate: false });
    }
  }, [selectedService, setValue, stagePath, itemPath, watch]);

  useEffect(() => {
    if (selectedServiceCategory.length > 0) {
      setValue(`${stagePath}.categoria`, selectedServiceCategory[0].name);
      setValue((`${itemPath}.serviceCategoryId`) as any, selectedServiceCategory[0].id, { shouldDirty: true, shouldValidate: false });
    }
  }, [selectedServiceCategory, setValue, stagePath]);

  useEffect(() => {
    if (selectedServiceType.length > 0) {
      setValue(`${stagePath}.tipo`, selectedServiceType[0].name);
      setValue((`${itemPath}.serviceTypeId`) as any, selectedServiceType[0].id, { shouldDirty: true, shouldValidate: false });
    }
  }, [selectedServiceType, setValue, stagePath]);

  useEffect(() => {
    if (selectedSupplier.length > 0) {
      setValue(`${itemPath}.stage.fornecedor`, selectedSupplier[0].name);
      setValue((`${itemPath}.serviceSupplierCompanyId`) as any, selectedSupplier[0].id, { shouldDirty: true, shouldValidate: false });
    }
  }, [selectedSupplier, setValue, itemPath]);

  useEffect(() => {
    if (!isSubItem) return;
    const qty = Number(watch(`${itemPath}.stage.quantidade`));
    if (!isNaN(qty)) {
      setValue((`${itemPath}.quantity`) as any, qty, { shouldDirty: true, shouldValidate: false });
    }
  }, [watch(`${itemPath}.stage.quantidade`), isSubItem]);

  useEffect(() => {
    const existingStageId = watch((`${itemPath}.stageId`) as any) as number | undefined;
    const scId = watch((`${itemPath}.serviceCategoryId`) as any) as number | undefined;
    const stId = watch((`${itemPath}.serviceTypeId`) as any) as number | undefined;
    const ssId = watch((`${itemPath}.serviceSupplierCompanyId`) as any) as number | undefined;
    if (existingStageId && selectedService.length === 0) {
      setSelectedService([{ id: existingStageId, name: watch(`${stagePath}.servico`) || '' }]);
    }
    if (scId && selectedServiceCategory.length === 0) {
      setSelectedServiceCategory([{ id: scId, name: watch(`${stagePath}.categoria`) || '' }]);
    }
    if (stId && selectedServiceType.length === 0) {
      setSelectedServiceType([{ id: stId, name: watch(`${stagePath}.tipo`) || '' }]);
    }
    if (ssId && selectedSupplier.length === 0) {
      setSelectedSupplier([{ id: ssId, name: watch(`${itemPath}.stage.fornecedor`) || '' }]);
    }
  }, [watch, itemPath, stagePath, selectedService.length, selectedServiceCategory.length, selectedServiceType.length, selectedSupplier.length]);

  const wStageId = useWatch({ control, name: (`${itemPath}.stageId` as any) });
  const wStageName = useWatch({ control, name: (`${stagePath}.servico` as any) });
  const wCatId = useWatch({ control, name: (`${itemPath}.serviceCategoryId` as any) });
  const wCatName = useWatch({ control, name: (`${stagePath}.categoria` as any) });
  const wTypeId = useWatch({ control, name: (`${itemPath}.serviceTypeId` as any) });
  const wTypeName = useWatch({ control, name: (`${stagePath}.tipo` as any) });
  const wSuppId = useWatch({ control, name: (`${itemPath}.serviceSupplierCompanyId` as any) });
  const wSuppName = useWatch({ control, name: (`${itemPath}.stage.fornecedor` as any) });

  useEffect(() => {
    if (wStageId && selectedService.length === 0) setSelectedService([{ id: Number(wStageId), name: String(wStageName || '') }]);
    if (wCatId && selectedServiceCategory.length === 0) setSelectedServiceCategory([{ id: Number(wCatId), name: String(wCatName || '') }]);
    if (wTypeId && selectedServiceType.length === 0) setSelectedServiceType([{ id: Number(wTypeId), name: String(wTypeName || '') }]);
    if (wSuppId && selectedSupplier.length === 0) setSelectedSupplier([{ id: Number(wSuppId), name: String(wSuppName || '') }]);

    const fetchMissingNames = async () => {
      try {
        if (wCatId && !wCatName) {
          const res = await fetch(`${API_BASE_URL}/select-in-order/service-categories`);
          const cats = await res.json();
          const cat = (Array.isArray(cats) ? cats : []).find((c: any) => Number(c.id) === Number(wCatId));
          if (cat?.name) {
            setValue((`${stagePath}.categoria`) as any, String(cat.name), { shouldDirty: false, shouldValidate: false });
            if (selectedServiceCategory.length === 0) setSelectedServiceCategory([{ id: Number(wCatId), name: String(cat.name) }]);
          }
        }
        if (wTypeId && !wTypeName) {
          const res = await fetch(`${API_BASE_URL}/select-in-order/service-types`);
          const types = await res.json();
          const tp = (Array.isArray(types) ? types : []).find((t: any) => Number(t.id) === Number(wTypeId));
          if (tp?.name) {
            setValue((`${stagePath}.tipo`) as any, String(tp.name), { shouldDirty: false, shouldValidate: false });
            if (selectedServiceType.length === 0) setSelectedServiceType([{ id: Number(wTypeId), name: String(tp.name) }]);
          }
        }
        if (wSuppId && !wSuppName) {
          let supplierName: string | undefined = undefined;
          if (wStageId) {
            const params = new URLSearchParams({ stageId: String(wStageId), limit: '100' });
            const res = await fetch(`${API_BASE_URL}/select-in-order/service-suppliers?${params.toString()}`);
            const data = await res.json();
            const list = Array.isArray(data?.suppliers) ? data.suppliers : Array.isArray(data) ? data : [];
            const found = list.find((s: any) => Number(s.id) === Number(wSuppId));
            supplierName = found?.name || found?.companyName;
          }
          if (!supplierName) {
            const resAll = await fetch(`${API_BASE_URL}/select-in-order/suppliers`);
            const dataAll = await resAll.json();
            const listAll = Array.isArray(dataAll?.suppliers) ? dataAll.suppliers : Array.isArray(dataAll) ? dataAll : [];
            const foundAll = listAll.find((s: any) => Number(s.id) === Number(wSuppId));
            supplierName = foundAll?.name || foundAll?.companyName;
          }
          if (supplierName) {
            setValue((`${itemPath}.stage.fornecedor`) as any, String(supplierName), { shouldDirty: false, shouldValidate: false });
            if (selectedSupplier.length === 0) setSelectedSupplier([{ id: Number(wSuppId), name: String(supplierName) }]);
          }
        }
      } catch (e) { void e; }
    };
    fetchMissingNames();
  }, [wStageId, wStageName, wCatId, wCatName, wTypeId, wTypeName, wSuppId, wSuppName, selectedService.length, selectedServiceCategory.length, selectedServiceType.length, selectedSupplier.length]);

  return (
    <>
      <FormGrid columns={isSimpleService ? 3 : 3} style={{ marginTop: '16px' }}>
        <div>
          <SteelOrdersInfiniteSelect
            label="Serviço"
            items={selectedService}
            setItems={setSelectedService}
            endpoint="services"
            placeholder="Procure por nome ou código"
            additionalParams={parentUnitId ? { unitOfMeasurementId: Number(parentUnitId) } : {}}
            key={parentUnitId ? `services-unit-${parentUnitId}` : 'services-all'}
            onCleared={() => {
              setValue((`${itemPath}.stageId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
              setValue((`${stagePath}.servico`) as any, '', { shouldDirty: true, shouldValidate: false });
              setValue(`${itemPath}.priceDetails.unitPrice` as any, '', { shouldDirty: true, shouldValidate: false });
              setValue((`${itemPath}.cost`) as any, undefined, { shouldDirty: true, shouldValidate: false });
              setValue((`${itemPath}.markup`) as any, undefined, { shouldDirty: true, shouldValidate: false });
              setValue((`${itemPath}.costUnitId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
              setValue((`${itemPath}.costUnitName`) as any, '', { shouldDirty: true, shouldValidate: false });
              setValue((`${itemPath}.itemPrice`) as any, undefined, { shouldDirty: true, shouldValidate: false });
              setSelectedServiceCategory([]);
              setSelectedServiceType([]);
              setSelectedSupplier([]);
            }}
          />
          {selectedWithoutCostInUnit && (
            <PendencyCallout
              message={parentUnitName ? `Serviço sem custo em ${parentUnitName} no cadastro — preço não calculado` : 'Serviço sem custo na unidade do item no cadastro — preço não calculado'}
              actionLabel="Cadastrar custo"
              onAction={() => setCostModalMode('cost')}
            />
          )}
          {missingCostInUnit && (
            <ZeroCostRow>
              <ZeroCostSwitch>
                <ZeroCostInput
                  type="checkbox"
                  id={`${itemPath}-allow-zero-cost`}
                  checked={allowsZeroCost}
                  disabled={zeroCostSaving}
                  onChange={(e) => handleAllowZeroCostToggle(e.target.checked)}
                />
                <ZeroCostSlider />
              </ZeroCostSwitch>
              <ZeroCostLabel htmlFor={`${itemPath}-allow-zero-cost`}>
                Sem cobrança
              </ZeroCostLabel>
            </ZeroCostRow>
          )}
          {selectedWithoutMarkup && (
            <PendencyCallout
              message="Serviço sem markup no cadastro — preço não calculado"
              actionLabel="Informar markup"
              onAction={() => setCostModalMode('markup')}
            />
          )}
          <QuickStageCostModal
            isOpen={costModalMode !== null}
            onClose={() => setCostModalMode(null)}
            stageId={Number(selectedService[0]?.id) || 0}
            stageName={selectedService[0]?.name || 'Serviço'}
            unitId={Number(parentUnitId) || 0}
            unitName={parentUnitName || 'na unidade do item'}
            needsMarkup={!hasMarkupSnapshot}
            markupOnly={costModalMode === 'markup'}
            onSaved={handleQuickCostSaved}
          />
        </div>
        {isSimpleService ? (
          <>
            <FormGroup>
              <Label>Preço do Serviço</Label>
              <Controller
                name={`${itemPath}.priceDetails.itemSubtotal` as const}
                control={control}
                render={({ field }) => (
                  <CurrencyInput
                    id={`${itemPath}.priceDetails.itemSubtotal`}
                    name={`${itemPath}.priceDetails.itemSubtotal`}
                    value={field.value || 0}
                    onValueChange={(value: any, name: any, values: any) => {
                      const floatVal = values?.float || 0;
                      field.onChange(floatVal);
                      setValue(`${itemPath}.priceDetails.itemSubtotal` as any, floatVal, { shouldDirty: true, shouldValidate: false });
                      setValue((`${itemPath}.itemPrice`) as any, floatVal, { shouldDirty: true, shouldValidate: false });
                    }}
                    placeholder="R$ 0,00"
                    hasError={!!get(errors, `${itemPath}.priceDetails.itemSubtotal`)}
                  />
                )}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor={`${stagePath}.observacoes`}>Observações</Label>
              <Textarea
                id={`${stagePath}.observacoes`}
                {...register(`${stagePath}.observacoes` as const)}
                placeholder="Digite aqui"
              />
            </FormGroup>
          </>
        ) : (
          <>
            <SteelOrdersInfiniteSelect
              label="Categoria"
              items={selectedServiceCategory}
              setItems={setSelectedServiceCategory}
              endpoint="service-categories"
              placeholder="Selecione a categoria"
              additionalParams={selectedService[0]?.id ? { stageId: selectedService[0].id } : {}}
              searchPlaceholder={selectedService[0]?.id ? 'Nenhuma categoria encontrada' : 'Selecione um serviço primeiro'}
              isDisabled={!selectedService[0]?.id}
              key={selectedService[0]?.id ? `service-categories-${selectedService[0].id}` : 'service-categories-disabled'}
              onCleared={() => {
                setValue((`${itemPath}.serviceCategoryId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
                setValue((`${stagePath}.categoria`) as any, '', { shouldDirty: true, shouldValidate: false });
              }}
            />
            <SteelOrdersInfiniteSelect
              label="Tipo"
              items={selectedServiceType}
              setItems={setSelectedServiceType}
              endpoint="service-types"
              placeholder="Selecione o tipo"
              additionalParams={selectedService[0]?.id && selectedServiceCategory[0]?.id ? { stageId: selectedService[0].id, categoryId: selectedServiceCategory[0].id } : {}}
              searchPlaceholder={selectedService[0]?.id ? (selectedServiceCategory[0]?.id ? 'Nenhum tipo encontrado' : 'Selecione uma categoria primeiro') : 'Selecione um serviço primeiro'}
              isDisabled={!selectedService[0]?.id || !selectedServiceCategory[0]?.id}
              key={selectedService[0]?.id && selectedServiceCategory[0]?.id ? `service-types-${selectedService[0].id}-${selectedServiceCategory[0].id}` : 'service-types-disabled'}
              onCleared={() => {
                setValue((`${itemPath}.serviceTypeId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
                setValue((`${stagePath}.tipo`) as any, '', { shouldDirty: true, shouldValidate: false });
              }}
            />
          </>
        )}
      </FormGrid>

      {allowsZeroCost && !!wStageId && (
        <ZeroCostNotice>
          Serviço sem cobrança — custo, markup e preço saem zerados e não entram no total.
        </ZeroCostNotice>
      )}

      {(!!wStageId || hasCostSnapshot) && !allowsZeroCost && (
        <FormGrid columns={3} style={{ marginTop: '16px' }}>
          <FormGroup>
            <Label htmlFor={`${itemPath}.cost`}>Custo{watchedCostUnitName ? ` (${watchedCostUnitName})` : ''}</Label>
            <Controller
              name={`${itemPath}.cost` as any}
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  id={`${itemPath}.cost`}
                  name={`${itemPath}.cost`}
                  value={(field.value as number) || 0}
                  onValueChange={(value: any, name: any, values: any) => {
                    field.onChange(values?.float);
                  }}
                  placeholder="R$ 0,00"
                  hasError={!!get(errors, `${itemPath}.cost`)}
                />
              )}
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor={`${itemPath}.markup`}>Markup</Label>
            <Input
              id={`${itemPath}.markup`}
              autoComplete="off"
              type="number"
              step="0.01"
              {...register(`${itemPath}.markup` as any, { valueAsNumber: true })}
              placeholder="Ex: 1.5"
              hasError={!!get(errors, `${itemPath}.markup`)}
            />
          </FormGroup>
          {!isSimpleService ? (
            <FormGroup>
              <Label htmlFor={`${itemPath}.itemPrice`}>Preço do Serviço</Label>
              <Controller
                name={`${itemPath}.itemPrice` as any}
                control={control}
                render={({ field }) => (
                  <CurrencyInput
                    id={`${itemPath}.itemPrice`}
                    name={`${itemPath}.itemPrice`}
                    value={(field.value as number) || 0}
                    onValueChange={(value: any, name: any, values: any) => {
                      const floatVal = values?.float || 0;
                      field.onChange(floatVal);
                      setValue(`${itemPath}.priceDetails.itemSubtotal` as any, floatVal, { shouldDirty: true, shouldValidate: false });
                    }}
                    placeholder="R$ 0,00"
                    hasError={!!get(errors, `${itemPath}.itemPrice`)}
                  />
                )}
              />
            </FormGroup>
          ) : (
            <FormGroup />
          )}
        </FormGrid>
      )}

      {!isSimpleService && (
        <FormGrid columns={3} style={{ marginTop: '16px' }}>

          <SteelOrdersInfiniteSelect
            label="Fornecedor"
            items={selectedSupplier}
            setItems={setSelectedSupplier}
            endpoint="service-suppliers"
            placeholder="Procure por fornecedor do serviço"
            searchPlaceholder={selectedService[0]?.id ? 'Nenhum fornecedor encontrado' : 'Selecione um serviço primeiro'}
            additionalParams={selectedService[0]?.id ? { stageId: selectedService[0].id } : {}}
            key={selectedService[0]?.id || 'service-supplier'}
            isDisabled={!selectedService[0]?.id}
            onCleared={() => {
              setValue((`${itemPath}.serviceSupplierCompanyId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
              setValue((`${itemPath}.stage.fornecedor`) as any, '', { shouldDirty: true, shouldValidate: false });
            }}
          />
          {isSubItem && (
            <FormGroup>
              <Label htmlFor={`${stagePath}.quantidade`}>
                Quantidade{parentUnitName ? ` (${parentUnitName})` : ''}
              </Label>
              <NumericInput
                id={`${itemPath}.stage.quantidade`}
                name={`${itemPath}.stage.quantidade`}
                value={Number.isFinite(Number(parentQty)) ? (parentQty as any) : ''}
                onChange={() => { }}
                onBlur={() => { }}
                hasError={false}
                disabled
              />
              <HintText>Acoplada à quantidade do item.</HintText>
            </FormGroup>
          )}
          <FormGroup>
            <Label>Arquivo</Label>
            <AttachmentInputContainer>
              <AttachmentTrigger onClick={() => setIsSourceChoiceOpen(true)} hasError={!!attachmentError}>
                <Paperclip size={16} />
                <AttachmentPlaceholder>Clique para anexar arquivo</AttachmentPlaceholder>
              </AttachmentTrigger>
              {attachments.length > 0 && (
                <TrashIconContainer
                  as="button"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemoveAllAttachments();
                  }}
                >
                  <Trash2 size={16} />
                </TrashIconContainer>
              )}
            </AttachmentInputContainer>
            {attachments.length > 0 && (
              <AttachmentList>
                {attachments.map((file, index) => (
                  <AttachmentListItem key={index}>
                    {file.name}
                    <RemoveAttachmentButton
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemoveAttachment(index);
                      }}
                    >
                      <X size={12} />
                    </RemoveAttachmentButton>
                  </AttachmentListItem>
                ))}
              </AttachmentList>
            )}
            <input
              type="file"
              multiple
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              accept=".pdf,.xml,.doc,.docx,.xls,.xlsx,.jpeg,.jpg,.png"
            />
            {attachmentError && <FormErrorMessage>{attachmentError}</FormErrorMessage>}

            <AttachmentSourceModal
              isOpen={isSourceChoiceOpen}
              onClose={() => setIsSourceChoiceOpen(false)}
              title="Adicionar Anexo ao Item"
              description="Escolha como deseja adicionar os arquivos ao item:"
              onChooseUpload={() => {
                handleFileSelectClick();
              }}
              onChooseGallery={() => {
                setIsGalleryOpen(true);
              }}
            />

            <AttachmentGalleryModal
              isOpen={isGalleryOpen}
              onClose={() => setIsGalleryOpen(false)}
              multiple={true}
              title="Galeria da Empresa - Anexos"
              onSelect={(files: GalleryFile[]) => {
                handleSelectFromGallery(files);
              }}
            />
          </FormGroup>
        </FormGrid>
      )}
    </>
  );
};

export default ServicesSection;
